import { Controller } from "@hotwired/stimulus";

// A quick collapse/fade for the rows of the balance ledger that appear or
// disappear when its Turbo Frame is answered with another state (kind
// filters, "Skrýt nulové", zoom). The server stays the source of truth: the
// frame is rendered exactly as without this controller; around Turbo's own
// swap we only remember the old rows, and put the ones that are gone back as
// inert copies that collapse and remove themselves, while the new ones expand.
//
// Markup: `data-motion-key` on every row that may appear or disappear, and
// `data-motion-list` on the element that holds such rows (the frame itself and
// the box of the entries of a group). Nothing animates when the user prefers
// reduced motion or when <body data-motion="off"> (the test environment, so
// the specs never see half-collapsed rows).
const DURATION = 300;

export default class extends Controller {
  connect() {
    this.clearLeaving = () => this.element.querySelectorAll("[data-motion-leaving]").forEach((el) => el.remove());
    document.addEventListener("turbo:before-cache", this.clearLeaving);
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.clearLeaving);
  }

  beforeRender(event) {
    // Nested frames (a note form, the details of an event) bubble this event.
    if (event.target !== this.element || !this.animates) return;
    event.detail.render = (current, incoming) => this.render(current, incoming);
  }

  get animates() {
    return (
      typeof Element.prototype.animate === "function" &&
      document.body.dataset.motion !== "off" &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }

  render(current, incoming) {
    const before = this.snapshot(current);

    // What Turbo's FrameRenderer does by default.
    const range = document.createRange();
    range.selectNodeContents(current);
    range.deleteContents();
    const source = incoming.ownerDocument.createRange();
    source.selectNodeContents(incoming);
    current.appendChild(source.extractContents());

    const after = this.snapshot(current);
    this.restoreLeaving(before, after);
    this.expandEntering(before, after);
  }

  // The rows with a key, by the list they are in (in order), and all keys.
  snapshot(root) {
    const lists = new Map();
    const keys = new Set();
    const listOf = (el) => {
      const list = el.closest("[data-motion-list]");
      const key = list.dataset.motionList;
      if (!lists.has(key)) lists.set(key, { el: list, items: [] });
      return lists.get(key);
    };
    // The frame is the root list; the lists inside it follow.
    [root, ...root.querySelectorAll("[data-motion-list]")].forEach(listOf);
    root.querySelectorAll("[data-motion-key]").forEach((el) => {
      const key = el.dataset.motionKey;
      listOf(el.parentElement).items.push({ key, el, copy: el.cloneNode(true) });
      keys.add(key);
    });
    return { lists, keys };
  }

  restoreLeaving(before, after) {
    before.lists.forEach((oldList, listKey) => {
      const newList = after.lists.get(listKey);
      if (!newList) return; // the whole list is gone with its row
      const kept = new Map(newList.items.map((item) => [item.key, item.el]));
      let anchor = null;
      let waiting = [];
      oldList.items.forEach((item) => {
        if (kept.has(item.key)) {
          anchor = kept.get(item.key);
          waiting.forEach((copy) => anchor.before(copy));
          waiting = [];
        } else {
          const copy = this.leavingCopy(item.copy);
          if (anchor) {
            anchor.after(copy);
            anchor = copy;
          } else {
            waiting.push(copy);
          }
        }
      });
      waiting.forEach((copy) => newList.el.append(copy));
    });
    // Collapse only after everything is placed, so the heights are final.
    this.element.querySelectorAll("[data-motion-leaving]").forEach((copy) => this.collapse(copy));
  }

  leavingCopy(copy) {
    copy.setAttribute("data-motion-leaving", "");
    copy.setAttribute("aria-hidden", "true");
    copy.setAttribute("inert", "");
    // A copy must not load, mount or collide with anything of the real rows.
    [copy, ...copy.querySelectorAll("*")].forEach((el) => {
      el.removeAttribute("id");
      el.removeAttribute("data-controller");
      el.removeAttribute("data-motion-key");
      el.removeAttribute("data-motion-list");
      if (el.localName === "turbo-frame") el.removeAttribute("src");
    });
    return copy;
  }

  expandEntering(before, after) {
    after.lists.forEach((newList, listKey) => {
      if (!before.lists.has(listKey)) return; // appears with its parent row
      newList.items.forEach((item) => {
        if (!before.keys.has(item.key)) this.expand(item.el);
      });
    });
  }

  keyframes(el, collapsing) {
    const style = getComputedStyle(el);
    const open = {
      height: `${el.offsetHeight}px`,
      paddingTop: style.paddingTop,
      paddingBottom: style.paddingBottom,
      borderTopWidth: style.borderTopWidth,
      opacity: 1,
      overflow: "hidden",
    };
    const closed = { height: "0px", paddingTop: "0px", paddingBottom: "0px", borderTopWidth: "0px", opacity: 0, overflow: "hidden" };
    return collapsing ? [open, closed] : [closed, open];
  }

  collapse(el) {
    const animation = el.animate(this.keyframes(el, true), { duration: DURATION, easing: "ease", fill: "forwards" });
    animation.onfinish = animation.oncancel = () => el.remove();
  }

  expand(el) {
    el.animate(this.keyframes(el, false), { duration: DURATION, easing: "ease" });
  }
}
